import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { ListingForm } from '../listing-form/listing-form';
import { Instructions } from '../instructions/instructions';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { DocumentPreview } from "../../../../../../shared/general/components/document-preview/document-preview";
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DocumentService } from '../../../../../../shared/general/services/document.service';
import { MatCardModule } from '@angular/material/card';
import { ListingFormReview } from '../listing-form-review/listing-form-review';
import { ListingFormSuccess } from "../listing-form-success/listing-form-success";
import { SalesConsultantListingFormRequest, SalesConsultantListingFormResponse } from '../../../../models/sales-consultant-listing-form-request.model';
import { DialogService } from '../../../../../../shared/services/dialog.service';
import confetti from 'canvas-confetti';
import { HapticsService } from '../../../../../../shared/general/services/haptics.service';
import { LoaderComponent } from '../../../../../../shared/general/components/loader/loader.component';
import { GtmService } from '../../../../../../core/services/gtm.service';

type WorkflowState = 'INSTRUCTIONS' | 'FILLING_FORM' | 'PREVIEW_GENERATED' | 'SUCCESS';

@Component({
  selector: 'app-listing-form-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressBarModule,
    MatIconModule,
    MatCardModule,
    MatProgressSpinnerModule,
    Instructions,
    DocumentPreview,
    ListingForm,
    ListingFormReview,
    ListingFormReview,
    ListingFormSuccess,
    LoaderComponent
],
  templateUrl: './listing-form-dialog.html',
  styleUrl: './listing-form-dialog.scss',
})
export class ListingFormDialog implements OnInit, AfterViewInit {
  private cdr = inject(ChangeDetectorRef);
  private dialogService = inject(DialogService);
  private gtm = inject(GtmService);
  private haptics = inject(HapticsService);
  private platformId = inject(PLATFORM_ID);

  currentState = signal<WorkflowState>('INSTRUCTIONS');
  pdfUrlForPreview: string | ArrayBuffer | Uint8Array | null = "/assets/files/Sales_Consultant_Listing_Form.pdf";
  pdfUrlForApplied: string | ArrayBuffer | Uint8Array | null = "/assets/files/Sales_Consultant_Listing_Form.pdf";
  isCurrentStepValid = signal(false);
  stepOneData: any = null;
  downloadFormLink = '';

  currentStep = 1;
  isLoading = signal(false);
  formData: any = null;
  private documentService = inject(DocumentService);
  
  private dialogRef = inject(MatDialogRef<ListingFormDialog>);
  public data = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.gtm.pageView({
      page_path: '/common-forms/listing-form',
      page_title: 'Horizinns | Sales Consultant Listing'
    });
  } 

  ngAfterViewInit() {
    setTimeout(() => {
      this.cdr.markForCheck();
    }, 300);
  }
  
  onClose() {
    console.log('onClose()');
    if (this.currentStep === 1 || this.currentStep === 4) {
      this.dialogRef.close();
      return;
    }

    const confirmRef = this.dialogService.confirm(
      'You have unsaved changes in your listing form. Are you sure you want to exit?',
      'Discard Changes?',
      'Discard',
      'Keep Editing'
    ).subscribe(result => {
      if (result) {
        this.documentService.clearFormState();
        this.dialogRef.close();
      }
    });
  }

  goToForm() {
    console.log('goToForm()');
  }

  generatePdf(event: Event) {
    console.log('generatePdf');
  }

  finalize() {
    console.log('finalize');
  }

  handleValidity(isValid: boolean) {
    console.log(
      '[DIALOG RECEIVE] validity =', isValid,
      '| current step =', this.currentStep,
      '| button will be enabled =', !(this.isLoading() || (this.currentStep === 2 && !isValid))
    );

    queueMicrotask(() => {
      this.isCurrentStepValid.set(isValid);
      this.cdr.markForCheck();
    });
  }

  handleDataChange(data: any) {
    this.stepOneData = data;
  }

  async onNext() {
    this.haptics.click();
    console.log(`step ${this.currentStep} of listing form`);
    
    if (this.currentStep === 1) {
      this.currentStep = 2;
      if (this.formData) {
        this.isCurrentStepValid.set(true);
      }

      this.documentService.getListingTemplate().subscribe((response: any) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        this.pdfUrlForPreview = URL.createObjectURL(blob);
        this.cdr.detectChanges();
      });

      return;
    }

    if (this.currentStep === 2) {
      if (!this.formData) {
        console.error("Form data is missing!");
        return; 
      }
      // Transitioning from Form to Preview
      this.isLoading.set(true);
      console.log('formData', this.formData);
      const data = this.formData; 
    
      const request: SalesConsultantListingFormRequest = {
        base64Signature: data.host?.signatureImage,
        userId: data.host?.id,
        propertyId: data.address?.propertyId,
        signerName: data.host?.name,
        hostType: data.host?.role,
        hostName: data.host?.name,
        hostDate: data.host?.date,
        propertyStreet: data.address?.street,
        propertyCity: data.address?.city,
        propertyState: data.address?.state,
        propertyZip: data.address?.zip,
        consultantName: data.consultant?.name,
        consultantId: data.consultant?.id,
        consultantDate: data.consultant?.date
      };

      console.log('Sending Request:', request);
      this.documentService.previewListing(request).subscribe({
        next: (response: ArrayBuffer) => {
          this.haptics.success();
          const blob = new Blob([response], { type: 'application/pdf' });
          this.pdfUrlForPreview = URL.createObjectURL(blob);
          this.currentStep = 3;
          this.isLoading.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.haptics.error();
          console.error('API Error', err);
          this.isLoading.set(false);
        }
      });
    } else if (this.currentStep === 3) {
      console.log('apply', this.formData);
      
      this.isLoading.set(true);
      console.log('formData', this.formData);
      const data = this.formData; 
    
      const request: SalesConsultantListingFormRequest = {
        base64Signature: data.host?.signatureImage,
        userId: data.host?.id,
        propertyId: data.address?.propertyId,
        signerName: data.host?.name,
        hostType: data.host?.role,
        hostName: data.host?.name,
        hostDate: data.host?.date,
        propertyStreet: data.address?.street,
        propertyCity: data.address?.city,
        propertyState: data.address?.state,
        propertyZip: data.address?.zip,
        consultantName: data.consultant?.name,
        consultantId: data.consultant?.id,
        consultantDate: data.consultant?.date
      };

      console.log('Sending Request:', request);
      this.documentService.finalizeListing(request).subscribe({
        next: (response: SalesConsultantListingFormResponse) => {
          this.haptics.success();
          this.confetti();
          this.downloadFormLink = response.url;
          this.currentStep = 4;
          this.documentService.clearFormState();
          this.isLoading.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.haptics.error();
          console.error('API Error', err);
          this.isLoading.set(false);
        }
      });
    } else if (this.currentStep < 4) {
      this.currentStep++;
    } else {
      this.dialogRef.close(true);
    }
  }

  onBack(): void {
    this.haptics.click();
    if (this.currentStep > 1) {
      this.currentStep--;
      
      if (this.currentStep === 2) {
        if (this.formData) {
          this.isCurrentStepValid.set(true); 
        }
      }
      this.cdr.detectChanges();
    }
  }

  handleFormUpdate(data: any) {
    console.log('Dialog received fresh data:', data);
    this.formData = data;
  
    if (this.formData && !this.isCurrentStepValid()) {
        this.isCurrentStepValid.set(true);
    }
    this.cdr.detectChanges();
  }

  /**
   * Calculates the percentage for the mat-progress-bar.
   * (currentStep / totalSteps) * 100
   */
  get progressValue(): number {
    const totalSteps = 4;
    return (this.currentStep / totalSteps) * 100;
  }

  /**
   * Returns a user-friendly label based on the current step.
   */
  get stepName(): string {
    switch (this.currentStep) {
      case 1:
        return 'Overview';
      case 2:
        return 'Review & Sign Document'
      case 3:
        return 'Review & Submit';
      case 4:
        return 'Submission Complete';
      default:
        return 'Processing';
    }
  }

  public confetti() {
    if (isPlatformBrowser(this.platformId)) {
      const duration = 15 * 300;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 99999 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      var interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);
    }
  }
}